import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { teamApi } from "@/features/team/api/teamApi"
import { queryKeys } from "@/shared/api/queryClient"
import { useFarmStore } from "@/features/farms/stores/farmStore"
import { Card } from "@/shared/components/ui/Card"
import { Button } from "@/shared/components/ui/Button"
import { TeamMemberCard } from "@/features/team/components/TeamMemberCard"
import { TeamMemberForm } from "@/features/team/components/TeamMemberForm"
import { ResetPasswordModal } from "@/features/team/components/ResetPasswordModal"
import { Plus, X, Users, AlertTriangle, Copy, Check } from "lucide-react"
import type { FarmUserResponse, FarmUserCreateRequest, FarmUserUpdateRequest } from "@/shared/types/api"

export function TeamPage() {
  const { selectedFarm } = useFarmStore()
  const queryClient = useQueryClient()

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<FarmUserResponse | null>(null)
  const [deletingMember, setDeletingMember] = useState<FarmUserResponse | null>(null)
  const [resetPasswordMember, setResetPasswordMember] = useState<FarmUserResponse | null>(null)
  const [copiedFarmId, setCopiedFarmId] = useState(false)

  // Fetch team members
  const { data: teamData, isLoading, error } = useQuery({
    queryKey: queryKeys.team.all(selectedFarm?.id || ""),
    queryFn: () => teamApi.list(selectedFarm!.id),
    enabled: !!selectedFarm,
  })

  // Create team member mutation
  const createMutation = useMutation({
    mutationFn: (data: FarmUserCreateRequest) => teamApi.create(selectedFarm!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team.all(selectedFarm!.id) })
      setIsCreateModalOpen(false)
    },
  })

  // Update team member mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FarmUserUpdateRequest }) =>
      teamApi.update(selectedFarm!.id, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team.all(selectedFarm!.id) })
      setEditingMember(null)
    },
  })

  // Delete team member mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => teamApi.delete(selectedFarm!.id, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team.all(selectedFarm!.id) })
      setDeletingMember(null)
    },
  })

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) =>
      teamApi.resetPassword(selectedFarm!.id, id, { newPassword }),
    onSuccess: () => {
      setResetPasswordMember(null)
    },
  })

  // Copy farm ID to clipboard
  const copyFarmId = async () => {
    if (selectedFarm?.id) {
      await navigator.clipboard.writeText(selectedFarm.id)
      setCopiedFarmId(true)
      setTimeout(() => setCopiedFarmId(false), 2000)
    }
  }

  if (!selectedFarm) {
    return (
      <Card>
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="font-roboto mt-2 text-sm font-medium text-gray-900">No farm selected</h3>
          <p className="font-roboto mt-1 text-sm text-gray-500">
            Please select a farm to view team members.
          </p>
        </div>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="font-roboto mt-2 text-sm font-medium text-gray-900">Error loading team</h3>
          <p className="font-roboto mt-1 text-sm text-gray-500">
            Failed to load team members. Please try again.
          </p>
        </div>
      </Card>
    )
  }

  const members = teamData?.data || []

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-roboto text-2xl font-bold text-gray-900">Team</h1>
          <p className="font-roboto mt-1 text-sm text-gray-600">
            Manage team members for {selectedFarm.name}
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Team Member
        </Button>
      </div>

      {/* Farm ID Info Card */}
      <Card className="bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="font-roboto text-sm font-medium text-gray-700">Farm ID for Worker Login</p>
            <p className="font-roboto text-xs text-gray-500 mt-0.5">
              Workers need this ID along with their username and password to log in.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <code className="px-3 py-1.5 bg-white rounded border border-slate-200/50 font-mono text-sm text-gray-700">
              {selectedFarm.id}
            </code>
            <button
              onClick={copyFarmId}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white"
              title="Copy Farm ID"
            >
              {copiedFarmId ? (
                <Check className="h-4 w-4 text-emerald-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </Card>

      {/* Team Members Grid */}
      {members.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {members.map((member) => (
            <TeamMemberCard
              key={member.id}
              member={member}
              onEdit={setEditingMember}
              onDelete={setDeletingMember}
              onResetPassword={setResetPasswordMember}
            />
          ))}
        </div>
      ) : (
        <Card>
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="font-roboto mt-2 text-sm font-medium text-gray-900">No team members yet</h3>
            <p className="font-roboto mt-1 text-sm text-gray-500">
              Add team members to give them access to this farm.
            </p>
            <Button onClick={() => setIsCreateModalOpen(true)} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Add First Team Member
            </Button>
          </div>
        </Card>
      )}

      {/* Create Modal */}
      {isCreateModalOpen && (
        <Modal title="Add Team Member" onClose={() => setIsCreateModalOpen(false)}>
          <TeamMemberForm
            onSubmit={async (data) => {
              await createMutation.mutateAsync(data as FarmUserCreateRequest)
            }}
            onCancel={() => setIsCreateModalOpen(false)}
            isLoading={createMutation.isPending}
          />
        </Modal>
      )}

      {/* Edit Modal */}
      {editingMember && (
        <Modal title="Edit Team Member" onClose={() => setEditingMember(null)}>
          <TeamMemberForm
            teamMember={editingMember}
            onSubmit={async (data) => {
              await updateMutation.mutateAsync({
                id: editingMember.id,
                data: data as FarmUserUpdateRequest,
              })
            }}
            onCancel={() => setEditingMember(null)}
            isLoading={updateMutation.isPending}
          />
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deletingMember && (
        <ConfirmModal
          title="Remove Team Member"
          message={`Are you sure you want to remove ${deletingMember.displayName || deletingMember.username}? They will no longer be able to access this farm.`}
          confirmLabel="Remove"
          confirmVariant="danger"
          onConfirm={async () => {
            await deleteMutation.mutateAsync(deletingMember.id)
          }}
          onCancel={() => setDeletingMember(null)}
          isLoading={deleteMutation.isPending}
        />
      )}

      {/* Reset Password Modal */}
      {resetPasswordMember && (
        <ResetPasswordModal
          isOpen={true}
          onClose={() => setResetPasswordMember(null)}
          onSubmit={async (newPassword) => {
            await resetPasswordMutation.mutateAsync({
              id: resetPasswordMember.id,
              newPassword,
            })
          }}
          memberName={resetPasswordMember.displayName || resetPasswordMember.username}
          isLoading={resetPasswordMutation.isPending}
        />
      )}
    </div>
  )
}

// Simple Modal Component
function Modal({
  title,
  children,
  onClose,
}: {
  title: string
  children: React.ReactNode
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b border-slate-200/50">
            <h2 className="font-roboto text-lg font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4">{children}</div>
        </div>
      </div>
    </div>
  )
}

// Simple Confirm Modal Component
function ConfirmModal({
  title,
  message,
  confirmLabel,
  confirmVariant = "primary",
  onConfirm,
  onCancel,
  isLoading,
}: {
  title: string
  message: string
  confirmLabel: string
  confirmVariant?: "primary" | "danger"
  onConfirm: () => Promise<void>
  onCancel: () => void
  isLoading: boolean
}) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm">
          <div className="p-6">
            <h2 className="font-roboto text-lg font-semibold text-gray-900">{title}</h2>
            <p className="font-roboto mt-2 text-sm text-gray-600">{message}</p>
            <div className="flex gap-3 mt-6">
              <Button
                onClick={onConfirm}
                variant={confirmVariant}
                disabled={isLoading}
                className="flex-1"
              >
                {isLoading ? "Processing..." : confirmLabel}
              </Button>
              <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
